import { useState, useEffect, useCallback, useRef } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { useKitchenStatus } from '../../hooks/useOrderingStatus';
import { useKitchenWebSocket } from '../../hooks/useKitchenWebSocket';

type OrderStatus = 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled';
type MealType = 'lunch' | 'breakfast' | 'midnight';

interface Order {
  id: number;
  employee_name: string;
  employee_number: string | null;
  order_type: string;
  meal_name: string | null;
  order_status: OrderStatus;
  toppings: string[];
  notes: string | null;
  created_at: string | null;
}

interface BreakfastOrder {
  id: number;
  employee_name: string;
  employee_number: string | null;
  is_guest: boolean;
  order_status: OrderStatus;
  notes: string | null;
  created_at: string | null;
  items: Array<{
    name: string;
    category: string;
    quantity: number;
    notes: string | null;
  }>;
}

interface BreakfastOrdersResponse {
  date: string;
  total_orders: number;
  orders: BreakfastOrder[];
}

export function OrdersPage() {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [mealType, setMealType] = useState<MealType>('breakfast');
  const [manualOverride, setManualOverride] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Get kitchen status for auto-switching
  const { kitchenStatus } = useKitchenStatus();

  // Play beep sound using Web Audio API
  const playBeep = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (error) {
      console.error('Error playing beep sound:', error);
    }
  }, []);

  // WebSocket connection for real-time updates
  const { isConnected, connectionError } = useKitchenWebSocket({
    enabled: true,
    onNewOrder: (message) => {
      console.log('New order received via WebSocket:', message);
      playBeep();
      // The hook already invalidates queries, so the list will auto-refresh
    },
    onOrderStatusUpdate: (message) => {
      console.log('Order status updated via WebSocket:', message);
      // The hook already invalidates queries, so the list will auto-refresh
    },
  });
  
  // Auto-switch meal type based on time (unless manually overridden)
  useEffect(() => {
    if (!manualOverride && kitchenStatus?.default_view) {
      setMealType(kitchenStatus.default_view);
    }
  }, [kitchenStatus?.default_view, manualOverride]);
  
  // Handle manual meal type change
  const handleMealTypeChange = (newType: MealType) => {
    setMealType(newType);
    setManualOverride(true);
  };
  
  // Reset to auto mode
  const resetToAuto = () => {
    setManualOverride(false);
    if (kitchenStatus?.default_view) {
      setMealType(kitchenStatus.default_view);
    }
  };

  const { data: lunchData, isLoading: lunchLoading, error: lunchError } = useAdmin.useTodaysOrders(selectedDate, mealType === 'midnight' ? 'midnight' : 'lunch', { refetchInterval: 60000, refetchIntervalInBackground: true });
  const { data: breakfastDataRaw, isLoading: breakfastLoading, error: breakfastError } = useAdmin.useTodaysBreakfastOrders(selectedDate);
  const updateLunchStatus = useAdmin.useUpdateOrderStatus();
  const updateBreakfastStatus = useAdmin.useUpdateBreakfastOrderStatus();

  const breakfastData = breakfastDataRaw as BreakfastOrdersResponse | undefined;
  const isLoading = mealType !== 'breakfast' ? lunchLoading : breakfastLoading;
  const error = mealType !== 'breakfast' ? lunchError : breakfastError;

  const handlePickedUp = async (orderId: number) => {
    try {
      if (mealType !== 'breakfast') {
        await updateLunchStatus.mutateAsync({ orderId, status: 'completed' });
      } else {
        await updateBreakfastStatus.mutateAsync({ orderId, status: 'completed' });
      }
    } catch (err) {
      console.error('Failed to update order status:', err);
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-yellow-100 text-yellow-800',
      ready: 'bg-green-100 text-green-800',
      completed: 'bg-gray-200 text-gray-600',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getOrderTypeSummary = () => {
    if (mealType !== 'breakfast') {
      if (!lunchData?.orders) return {};
      const summary: Record<string, number> = {};
      lunchData.orders.forEach((order: Order) => {
        summary[order.order_type] = (summary[order.order_type] || 0) + 1;
      });
      return summary;
    } else {
      // Breakfast - only count entrees, not beverages
      if (!breakfastData?.orders) return {};
      const summary: Record<string, number> = {};
      breakfastData.orders.forEach((order: BreakfastOrder) => {
        order.items?.forEach((item) => {
          // Only count entrees, skip beverages
          if (item.category === 'entree') {
            summary[item.name] = (summary[item.name] || 0) + item.quantity;
          }
        });
      });
      return summary;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hmh-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load orders. Please try again.</p>
      </div>
    );
  }

  const orderTypeSummary = getOrderTypeSummary();
  const totalOrders = mealType !== 'breakfast'
    ? (lunchData?.total_orders || 0)
    : (breakfastData?.total_orders || 0);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6 print:shadow-none">
        <div className="flex justify-between items-center mb-6 print:mb-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Kitchen Prep View</h1>

            {/* WebSocket Connection Status */}
            <div className="print:hidden flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} ${isConnected ? 'animate-pulse' : ''}`} />
              <span className="text-sm text-gray-600">
                {isConnected ? 'Live' : 'Disconnected'}
              </span>
              {connectionError && (
                <span className="text-xs text-red-600" title={connectionError}>
                  (Error)
                </span>
              )}
            </div>
          </div>

          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-hmh-blue text-white rounded hover:bg-blue-700 transition-colors print:hidden"
          >
            Print Orders
          </button>
        </div>

        <div className="flex gap-4 items-center flex-wrap mb-4">
          <div>
            <label htmlFor="order-date" className="block text-sm font-medium text-gray-700 mb-1">
              Order Date
            </label>
            <input
              type="date"
              id="order-date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-hmh-blue print:hidden"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meal Type
            </label>
            <div className="flex gap-2 items-center print:hidden">
              <button
                onClick={() => handleMealTypeChange('breakfast')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  mealType === 'breakfast'
                    ? 'bg-hmh-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Breakfast
              </button>
              <button
                onClick={() => handleMealTypeChange('lunch')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  mealType === 'lunch'
                    ? 'bg-hmh-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Lunch
              </button>
              <button
                onClick={() => handleMealTypeChange('midnight')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  mealType === 'midnight'
                    ? 'bg-indigo-900 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🌙 Midnight
              </button>
              {manualOverride && (
                <button
                  onClick={resetToAuto}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 underline"
                >
                  Reset to Auto
                </button>
              )}
            </div>
            {kitchenStatus && (
              <div className="text-xs text-gray-500 mt-1 print:hidden">
                {manualOverride ? (
                  <span className="text-amber-600">Manual mode</span>
                ) : (
                  <span className="text-green-600">Auto mode - {kitchenStatus.next_switch}</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4 flex-wrap">
          {mealType !== 'breakfast' ? (
            // Lunch: show Lunch, Salad, Special in that order
            ['Lunch', 'Salad', 'Special'].map((type) => (
              <div key={type} className="bg-gray-50 px-4 py-2 rounded border border-gray-200">
                <div className="text-sm text-gray-600">{type}</div>
                <div className="text-3xl md:text-4xl font-bold text-hmh-blue">{orderTypeSummary[type] || 0}</div>
              </div>
            ))
          ) : (
            // Breakfast: show entree counts (beverages filtered out)
            Object.entries(orderTypeSummary).map(([type, count]) => (
              <div key={type} className="bg-gray-50 px-4 py-2 rounded border border-gray-200">
                <div className="text-sm text-gray-600">{type}</div>
                <div className="text-3xl md:text-4xl font-bold text-hmh-blue">{count}</div>
              </div>
            ))
          )}
          <div className="bg-hmh-blue/10 px-4 py-2 rounded border border-hmh-blue">
            <div className="text-sm text-gray-600">Total Orders</div>
            <div className="text-3xl md:text-4xl font-bold text-hmh-blue">{totalOrders}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden print:shadow-none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {mealType !== 'breakfast' ? 'Employee' : 'Guest Name'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {mealType !== 'breakfast' ? 'Order Type' : 'Items'}
                </th>
                {mealType !== 'breakfast' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {mealType !== 'breakfast' ? 'Toppings' : 'Notes'}
                </th>
                {mealType !== 'breakfast' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mealType !== 'breakfast' ? (
                // Lunch orders
                lunchData?.orders && lunchData.orders.length > 0 ? (
                  lunchData.orders.map((order: Order) => (
                    <tr key={order.id} className="hover:bg-gray-50 print:hover:bg-white">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.employee_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.employee_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {order.order_type}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {order.meal_name || <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {order.toppings.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {order.toppings.map((topping, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                              >
                                {topping}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                        {order.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap print:hidden">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                          {order.order_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm print:hidden">
                        <button
                          onClick={() => handlePickedUp(order.id)}
                          disabled={order.order_status === 'completed' || updateLunchStatus.isPending}
                          className="px-4 py-2 bg-hmh-blue text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Picked Up
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      No lunch orders found for this date.
                    </td>
                  </tr>
                )
              ) : (
                // Breakfast orders
                breakfastData?.orders && breakfastData.orders.length > 0 ? (
                  breakfastData.orders.map((order: BreakfastOrder) => (
                    <tr key={order.id} className="hover:bg-gray-50 print:hover:bg-white">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.employee_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.employee_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {order.items?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {/* Entrees - blue */}
                            {order.items
                              .filter(item => item.category === 'entree')
                              .map((item, idx) => (
                                <span
                                  key={`entree-${idx}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {item.name} {item.quantity > 1 ? `(x${item.quantity})` : ''}
                                </span>
                              ))}
                            {/* Beverages - green */}
                            {order.items
                              .filter(item => item.category === 'beverage')
                              .map((item, idx) => (
                                <span
                                  key={`bev-${idx}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                                >
                                  {item.name} {item.quantity > 1 ? `(x${item.quantity})` : ''}
                                </span>
                              ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                        {order.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap print:hidden">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                          {order.order_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm print:hidden">
                        <button
                          onClick={() => handlePickedUp(order.id)}
                          disabled={order.order_status === 'completed' || updateBreakfastStatus.isPending}
                          className="px-4 py-2 bg-hmh-blue text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Picked Up
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No breakfast orders found for this date.
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:mb-4 {
            margin-bottom: 1rem !important;
          }
          .print\\:hover\\:bg-white:hover {
            background-color: white !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </div>
  );
}
